/** @jsx jsx */
import React, { useCallback, useMemo, type MouseEvent } from 'react';
import { cssMap, cx, jsx } from '@atlaskit/css';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';
import Anchor from '@atlaskit/primitives/anchor';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import HighlightText from '@atlassian/jira-software-highlight-text/src/ui/index.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { Key } from '../../../styled.tsx';
import { KEY_COMPONENT_SELECTOR } from '../../constants.tsx';

export type Props = {
	issueKey: string;
	issueUrl: string;
	isDone: boolean;
	highlightText: string | null;
	onClick: (e: MouseEvent, analyticsEvent: UIAnalyticsEvent) => void;
};

const KeyWithAnalytics = ({ issueUrl, issueKey, isDone, onClick, highlightText }: Props) => {
	const highlight = useMemo(() => (highlightText != null ? [highlightText] : []), [highlightText]);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const handleClick = useCallback(
		(e: MouseEvent) => {
			const analyticsEvent = createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'issue',
			});
			onClick(e, analyticsEvent);
		},
		[onClick, createAnalyticsEvent],
	);

	const handleOnContextMenu = useCallback((e: MouseEvent) => {
		e.stopPropagation();
	}, []);

	if (isVisualRefreshEnabled()) {
		return (
			<Anchor
				xcss={cx(keyStyles.default, isDone && keyStyles.done)}
				href={issueUrl}
				tabIndex={-1}
				onClick={handleClick}
				aria-hidden="true"
				onContextMenu={handleOnContextMenu}
				data-component-selector={KEY_COMPONENT_SELECTOR}
				testId={KEY_COMPONENT_SELECTOR}
			>
				{highlightText != null && highlightText !== '' && issueKey ? (
					<HighlightText text={issueKey} highlight={highlight} />
				) : (
					issueKey
				)}
			</Anchor>
		);
	}

	return (
		<Key
			href={issueUrl}
			tabIndex={-1}
			isDone={isDone}
			onClick={handleClick}
			aria-hidden="true"
			onContextMenu={handleOnContextMenu}
			data-component-selector={KEY_COMPONENT_SELECTOR}
			data-testid={KEY_COMPONENT_SELECTOR}
		>
			{highlightText != null && highlightText !== '' && issueKey ? (
				<HighlightText text={issueKey} highlight={highlight} />
			) : (
				issueKey
			)}
		</Key>
	);
};

export default KeyWithAnalytics;

const keyStyles = cssMap({
	default: {
		font: token('font.body'),
		fontVariantNumeric: 'tabular-nums',
		color: token('color.text.subtle'),
		outline: 'none',
		whiteSpace: 'nowrap',
		textDecoration: 'none',
		'&:hover': {
			textDecoration: 'underline',
		},
	},
	done: {
		textDecoration: 'line-through',
	},
});
